// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { CustomerConfig } from "utils/ConfigTypes";
import React from "react";
import { fbt } from "fbt";
import getSassStyle from "style/sassStyle";

export const NHSConfig: CustomerConfig = {
  id: "nhs",
  name: "NHS",
  pathPrefix: "nhs",
  allowGuestRuns: true,
  landingPage: "NEWTESTRUN",
  customThemeOverride: {},
  headerConfig: {
    headerLogo: { svg: "NHSlogo.svg", label: "NHS", position: "center" },
  },

  loginImageUrl: "NHSlogo.svg",
  loginHeader:
    "NHS OpenRDT enables you to order and conduct a COVID-19 test from the comfort of your home.",
  loginDescription:
    "The COVID-19 Antigen Home Testing App is offered by the NHS, and enables you to order and conduct a COVID-19 antigen test from the comfort of your home.",
  showProgressBar: false,
  getTestRunDefinition: () => ({
    gettingStarted: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Kit box and tissues on a table.",
              "starting step: image alt attribute",
              { project: "audere" }
            ),
            src: "gettingstarted.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Let's get started!", "starting step: page title", {
              project: "audere",
            }),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "This app will guide you through completing a nasal swab COVID-19 antigen test. The entire process will take about 20 minutes.",
              "starting step: intro text",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "Paragraph",
          props: {
            children: [
              <fbt desc="starting step: be careful" project="audere">
                It is very <strong>important</strong> that you complete the
                steps as <strong>carefully</strong> as possible to ensure the
                specimen can be processed.
              </fbt>,
            ],
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "Before you start:",
              "starting step: title leading to action list",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Find a well-lit location with a flat surface where you can do the test.",
                "starting step: well lit",
                { project: "audere" }
              ),
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "Or",
              "starting step: between to links as in 'Continue' or 'Skip to photo capture'",
              {
                project: "audere",
              }
            ),
          },
          wrapperStyle: {
            clear: "both",
            paddingTop: "16px",
          },
        },
        {
          type: "NextStepLink",
          props: {
            text: fbt(
              "Skip to photo capture.",
              "starting step: skip to photo link"
            ),
            toNav: "skipToPhoto",
            floatLeft: true,
            large: true,
          },
        },
      ],
      nav: {
        next: {
          default: "setupKit",
          skipToPhoto: "scanStrip",
        },
      },
    },

    setupKit: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Kit box, test vial, swab, buffer solution, and test cassette.",
              "Open test kit step: image alt attribute",
              { project: "audere" }
            ),
            src: "opentestkit.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Open your test kit", "Open test kit step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "Paragraph",
          props: {
            children: [
              fbt(
                "Open the test kit and remove the items. Handle all items gently, do not bend or squeeze. C",
                "Open test kit step: handle kit",
                {
                  project: "audere",
                }
              ),
              // Note: weird split on C|onfirm because unclear how else to get a space between components
              <fbt desc="Open test kit step: confirm items" project="audere">
                onfirm that the kit includes the items below, or{" "}
                <a href="mailto:support@auderenow.org">
                  contact us if an item is missing
                </a>.
              </fbt>,
            ],
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt("small bottle of buffer", "Open test kit step: buffer", {
                project: "audere",
              }),
              fbt("test cassette", "Open test kit step: cassette", {
                project: "audere",
              }),
              fbt("nasal swab", "Open test kit step: swab", {
                project: "audere",
              }),
              fbt("test vial", "Open test kit step: vial", {
                project: "audere",
              }),
              fbt("plastic waste bag", "Open test kit step: waste bag", {
                project: "audere",
              }),
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
      ],
      nav: {
        next: {
          default: "washHands",
        },
        previous: "gettingStarted",
      },
    },

    washHands: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 320,
            alt: fbt(
              "Soap being put on hands to be washed.",
              "wash hands step: alt description for image",
              { project: "audere" }
            ),
            src: "washhands.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Blow nose and wash hands", "wash hands step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "Paragraph",
          props: {
            content: fbt(
              "A clear nose and clean hands are important to obtain a good specimen.",
              "wash hands step: important for specimen",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Using a clean tissue, gently blow nose and remove any debris to clear both nostrils.",
                "wash hands step: blow nose",
                {
                  project: "audere",
                }
              ),
              fbt(
                "Wash hands with soap and warm water for 20 seconds and dry them thoroughly with a clean towel.",
                "wash hands step: wash and dry hands",
                {
                  project: "audere",
                }
              ),
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
      ],
      nav: {
        next: {
          default: "prepareTube",
        },
        previous: "setupKit",
      },
    },

    prepareTube: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            alt: fbt(
              "Buffer, test vial, and kit box.",
              "prepare vial step: alt description for image",
              { project: "audere" }
            ),
            src: "preparetest.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Prepare the test vial", "prepare vial step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Place the test vial in the hole in your kit box.",
                "prepare vial step: place vial",
                {
                  project: "audere",
                }
              ),
              fbt(
                "Open the bottle of buffer.",
                "prepare vial step: open buffer",
                {
                  project: "audere",
                }
              ),
              fbt(
                "Squeeze 5 drops of buffer into the test vial.",
                "prepare vial step: add buffer to vial",
                {
                  project: "audere",
                }
              ),
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
      ],
      nav: {
        next: {
          default: "swabNose",
        },
        previous: "washHands",
      },
    },
    swabNose: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 240,
            border: `0.5px solid ${getSassStyle().dividercolor}`,
            type: "local",
            alt: fbt(
              "Animated image of swabbing nose",
              "swab nose step: image description",
              { project: "audere" }
            ),
            src: "healthpulse/adult_nose_swab.gif",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Swab nose", "swab nose step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="swab nose step: insert swab" project="audere">
                Gently insert the <strong>entire</strong> soft tip of the swab
                into one nostril until you feel a bit of resistance (less than
                one inch into the nostril).
              </fbt>,
              <fbt desc="swab nose step: rub nostril" project="audere">
                Using medium pressure, rub the swab slowly in a circular motion
                around the inside wall of the nostril{" "}
                <strong>four times</strong>.
              </fbt>,
              <fbt
                desc="swab nose step: swab touching nostril"
                project="audere"
              >
                The swab tip should be touching the inside wall of the nostril
                through each rotation.
              </fbt>,
              <fbt desc="swab nose step: rub other nostril" project="audere">
                Repeat the same process with the <strong>same swab</strong> in
                the other nostril.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
      ],
      nav: {
        next: {
          default: "swabInTube",
        },
        previous: "prepareTube",
      },
    },

    swabInTube: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            type: "local",
            alt: fbt(
              "Swab swirling in test vial.",
              "swab in vial step: alt description for image",
              { project: "audere" }
            ),
            src: "swabInTube.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt("Put swab in the vial", "Swab in vial step: title", {
              project: "audere",
            }),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="Swab in vial step: swab in vial" project="audere">
                Place the swab into the vial. Make sure the swab tip is touching
                the bottom of the vial.
              </fbt>,
              <fbt desc="Swab in vial step: stir swab" project="audere">
                Stir it around five times in the vial.
              </fbt>,
              <fbt desc="Swab in vial step: squeeze vial" project="audere">
                Squeeze the vial around the swab tip.
              </fbt>,
              <fbt desc="Swab in vial step: break swab" project="audere">
                Snap off the end of the swab at the break line, so it fits
                within the vial.
              </fbt>,
              <fbt desc="Swab in vial step: seal vial" project="audere">
                Use the blue cap to seal the vial with the swab inside.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
      ],
      nav: {
        next: {
          default: "addLiquid",
        },
        previous: "swabNose",
      },
    },
    addLiquid: {
      contentDescription: [
        {
          type: "Asset",
          props: {
            height: 200,
            width: 300,
            type: "local",
            alt: fbt(
              "Solution being dropped into test cassette.",
              "Add liquid step: alt description for image",
              { project: "audere" }
            ),
            src: "solutionDrop.png",
          },
          display: "centered",
        },
        {
          type: "Title",
          props: {
            content: fbt(
              "Add vial contents to test cassette",
              "Add liquid step: title",
              {
                project: "audere",
              }
            ),
          },
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              <fbt desc="Add liquid step: place cassette" project="audere">
                Place the test cassette in front of you.
              </fbt>,
              <fbt desc="Add liquid step: open vial" project="audere">
                Open the bottom of the test vial.
              </fbt>,
              <fbt desc="Add liquid step: position vial" project="audere">
                Position the test vial just above the channel marked with an 'S'
                on the test cassette.
              </fbt>,
              <fbt desc="Add liquid step: squeeze vial" project="audere">
                Slowly and carefully squeeze 5 drops of the vial contents into
                the channel on the test cassette.
              </fbt>,
            ],
          },
        },
        {
          type: "NextStepLink",
          props: { floatLeft: true, large: true },
        },
      ],
      nav: {
        next: {
          default: "waitStripReaction",
        },
        previous: "swabInTube",
      },
    },
    waitStripReaction: {
      contentDescription: [
        {
          type: "Title",
          props: {
            content: fbt(
              "Please wait 15 minutes",
              "Wait reaction step: title",
              { project: "audere" }
            ),
          },
        },
        {
          type: "WaitStep",
          props: {},
        },
        {
          type: "DeclarativeList",
          props: {
            items: [
              fbt(
                "Once the timer is complete, you must take a photo of the test cassette within five minutes to ensure accurate results.",
                "Wait reaction step: once complete take photo",
                { project: "audere" }
              ),
            ],
          },
        },
        {
          type: "NextStepButton",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        next: {
          default: "scanStrip",
        },
        previous: "addLiquid",
      },
    },
    scanStrip: {
      contentDescription: [
        {
          type: "PhotoUploaderPanel",
          props: {},
        },
      ],
      hasFormContent: true,
      nav: {
        previous: "waitStripReaction",
      },
    },
  }),
};

export default NHSConfig;
